let plotlyDefaultOptions = {
  // "displayModeBar": true,
  scrollZoom: true,
  // editable: true,
  responsive: true,
  displaylogo: false,
  modeBarButtonsToRemove: [
    'resetAxisRanges',
    'lasso2d',
    'select2d',
    'resetScale2d',
    'hoverClosestCartesian',
    'hoverCompareCartesian',
    'toggleSpikelines'
  ],
  // 'modeBarButtonsToRemove': ['sendDataToCloud', 'lasso2d', 'select2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'resetScale2d', 'hoverClosestCartesian', 'hoverCompareCartesian', 'toggleSpikelines'],
  modeBarButtonsToAdd: [
    // "drawline",
    // "drawopenpath",
    // 'drawclosedpath',
    // "drawcircle",
    // "drawrect",
    // "sendDataToCloud",
    // 'eraseshape'
  ]
  // enable plotly studio to during development
  // showLink: true,
  // plotlyServerURL: "https://chart-studio.plotly.com"
};
// if theres a ?dev=true param override to add showLink (guard for tests)
if (
  typeof window !== 'undefined' &&
  window.location &&
  typeof window.location.search === 'string' &&
  window.location.search.indexOf('dev=true') > -1
) {
  plotlyDefaultOptions.showLink = true;
  plotlyDefaultOptions.plotlyServerURL = 'https://chart-studio.plotly.com';
}

export function getPlotlyLayoutDefaulted(plotlyJSON) {
  // Defensive check - if plotlyJSON is undefined or null, return an empty layout
  if (!plotlyJSON) {
    console.warn('plotlyJSON is undefined or null in getPlotlyLayoutDefaulted');
    return {
      font: {
        family: 'Roboto, sans-serif',
        size: 14,
        color: '#484848'
      }
    };
  }

  // Route payloads may include fixed export dimensions. Browser charts live in
  // resizable chat cards and dashboard cells, so keep the payload immutable and
  // let Plotly size each chart to its current container.
  var plotlyJSONLayout = Object.assign({}, plotlyJSON['layout'] || {});
  delete plotlyJSONLayout.width;
  delete plotlyJSONLayout.height;
  plotlyJSONLayout.autosize = true;

  // Set default font
  plotlyJSONLayout['font'] = plotlyJSONLayout['font'] || {
    family: 'Roboto, sans-serif',
    size: 14,
    color: '#484848'
  };

  // Set legend properties if legend exists
  if (plotlyJSONLayout['legend']) {
    plotlyJSONLayout['legend'] = Object.assign({}, plotlyJSONLayout['legend']);
    plotlyJSONLayout['legend']['tracegroupgap'] = 4;
    plotlyJSONLayout['legend']['bordercolor'] = 'rgba(68, 68, 68, 0.05)';
  }

  return plotlyJSONLayout;
}

const COMPACT_PLOT_MAX_WIDTH = 600;

function compactFont(font, defaultSize) {
  const source = font && typeof font === 'object' && !Array.isArray(font) ? font : {};
  const requestedSize = Number(source.size);
  return Object.assign({}, source, {
    size: Number.isFinite(requestedSize) && requestedSize > 0
      ? Math.min(requestedSize, defaultSize)
      : defaultSize
  });
}

function compactMargin(margin, key, fallback, maximum) {
  const requested = Number(margin && margin[key]);
  return Number.isFinite(requested) && requested >= 0
    ? Math.min(requested, maximum)
    : fallback;
}

function wrapCompactTitle(text, containerWidth) {
  if (typeof text !== 'string' || /<br\s*\/?\s*>|\n/i.test(text)) return text;
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return text;

  const maxCharacters = Math.max(24, Math.floor((containerWidth - 40) / 8));
  const lines = [];
  let line = '';
  words.forEach(word => {
    const candidate = line ? `${line} ${word}` : word;
    if (line && candidate.length > maxCharacters) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  return lines.join('<br>');
}

function compactAxis(axis) {
  if (!axis || typeof axis !== 'object' || Array.isArray(axis)) {
    axis = {};
  }

  const compact = Object.assign({ automargin: true }, axis);
  compact.tickfont = compactFont(axis.tickfont, 12);
  if (axis.title) {
    compact.title = typeof axis.title === 'string'
      ? { text: axis.title, font: { size: 14 }, standoff: 8 }
      : Object.assign(
        { standoff: 8 },
        axis.title,
        { font: compactFont(axis.title.font, 14) }
      );
  }
  return compact;
}

/**
 * Apply space-efficient defaults when Plotly is rendered in a narrow card.
 * Wide charts preserve the AI/user-authored layout. Narrow cards enforce
 * maximum margins and font sizes because desktop export values can otherwise
 * leave almost no plotting area on phones.
 */
export function getResponsivePlotlyLayout(layout, containerWidth) {
  const source = layout && typeof layout === 'object' && !Array.isArray(layout)
    ? layout
    : {};
  const width = Number(containerWidth);
  if (!Number.isFinite(width) || width <= 0 || width > COMPACT_PLOT_MAX_WIDTH) {
    return source;
  }

  const compact = Object.assign({}, source, { autosize: true });
  delete compact.width;
  delete compact.height;

  const sourceTitleText = typeof source.title === 'string'
    ? source.title
    : source.title && source.title.text;
  const compactTitleText = wrapCompactTitle(sourceTitleText, width);
  const compactTitleLines = typeof compactTitleText === 'string'
    ? compactTitleText.split(/<br\s*\/?\s*>|\n/i).length
    : 0;
  const titleTopMargin = source.title
    ? Math.min(112, 42 + Math.max(1, compactTitleLines) * 18)
    : 24;
  const legendBottomMargin = source.legend ? 120 : 52;

  compact.margin = {
    l: compactMargin(source.margin, 'l', 48, 56),
    r: compactMargin(source.margin, 'r', 16, 24),
    t: Math.max(compactMargin(source.margin, 't', titleTopMargin, 112), titleTopMargin),
    b: Math.max(compactMargin(source.margin, 'b', legendBottomMargin, 128), legendBottomMargin),
    pad: compactMargin(source.margin, 'pad', 2, 4)
  };
  compact.font = Object.assign(
    { family: 'Roboto, sans-serif', color: '#484848' },
    compactFont(source.font, 14)
  );

  if (source.title) {
    compact.title = typeof source.title === 'string'
      ? {
        text: compactTitleText,
        x: 0.5,
        xanchor: 'center',
        y: 0.97,
        yanchor: 'top',
        automargin: true,
        font: { size: 16 }
      }
      : Object.assign(
        { x: 0.5, xanchor: 'center', automargin: true },
        source.title,
        {
          text: compactTitleText,
          font: compactFont(source.title.font, 16),
          y: 0.97,
          yanchor: 'top'
        }
      );
  }

  if (source.legend) {
    compact.legend = Object.assign(
      {},
      source.legend,
      {
        orientation: 'h',
        x: 0,
        xanchor: 'left',
        y: -0.14,
        yanchor: 'top'
      }
    );
    compact.legend.font = compactFont(source.legend.font, 12);
    if (source.legend.title) {
      compact.legend.title = typeof source.legend.title === 'string'
        ? { text: source.legend.title, font: { size: 12 } }
        : Object.assign(
          {},
          source.legend.title,
          { font: compactFont(source.legend.title.font, 12) }
        );
    }
  }

  ['xaxis', 'yaxis'].forEach(axisName => {
    if (source[axisName]) compact[axisName] = compactAxis(source[axisName]);
  });

  return compact;
}

// Ensure Plotly relayout on container size changes (orientation/resize)
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  const triggerRelayout = () => {
    try {
      const plots = document.getElementsByClassName('js-plotly-plot');
      for (let i = 0; i < plots.length; i++) {
        const el = plots[i];
        if (el && el.id) {
          Promise.resolve(Plotly.relayout(el.id, {})).catch(() => {});
        } else if (el) {
          Plotly.Plots && Plotly.Plots.resize && Promise.resolve(Plotly.Plots.resize(el)).catch(() => {});
        }
      }
    } catch (e) {
      // ignore
    }
  };
  window.addEventListener('resize', triggerRelayout);
  window.addEventListener('orientationchange', triggerRelayout);
  // Back-compat: expose helper on window for legacy scripts/tests
  try {
    if (!window.getPlotlyLayoutDefaulted) {
      window.getPlotlyLayoutDefaulted = getPlotlyLayoutDefaulted;
    }
    if (!window.getResponsivePlotlyLayout) {
      window.getResponsivePlotlyLayout = getResponsivePlotlyLayout;
    }
    if (!window.plotlyDefaultOptions) {
      window.plotlyDefaultOptions = plotlyDefaultOptions;
    }
  } catch (e) {
    // ignore
  }
}
